import type { ZoneDefinition, ZoneSetKey, IntensityTarget, SportType } from './workout';

/**
 * Default zone tables — each set is CONTIGUOUS by construction:
 * the upper bound of zone N equals the lower bound of zone N+1. This invariant
 * is what `validateZones` enforces at runtime, so changing these tables must
 * preserve continuity or the validator will block generation/export.
 */

// Cycling — Power (% FTP) — Andrew Coggan (Classic).
export const DEFAULT_BIKE_ZONES_POWER: ZoneDefinition[] = [
  { id: 'Z1', name: 'Active Recovery',     min: 0.00, max: 0.55, color: '#9ca3af', method: 'Andrew Coggan (Classic)' },
  { id: 'Z2', name: 'Endurance',           min: 0.55, max: 0.75, color: '#3b82f6', method: 'Andrew Coggan (Classic)' },
  { id: 'Z3', name: 'Tempo',               min: 0.75, max: 0.90, color: '#22c55e', method: 'Andrew Coggan (Classic)' },
  { id: 'Z4', name: 'Lactate Threshold',   min: 0.90, max: 1.05, color: '#eab308', method: 'Andrew Coggan (Classic)' },
  { id: 'Z5', name: 'VO2 Max',             min: 1.05, max: 1.20, color: '#ef4444', method: 'Andrew Coggan (Classic)' },
  { id: 'Z6', name: 'Anaerobic Capacity',  min: 1.20, max: 1.50, color: '#a855f7', method: 'Andrew Coggan (Classic)' },
  { id: 'Z7', name: 'Neuromuscular Power', min: 1.50, max: 2.50, color: '#ec4899', method: 'Andrew Coggan (Classic)' },
];

// Cycling — HR (% LTHR) — Joe Friel / TrainingPeaks.
export const DEFAULT_BIKE_ZONES_HR: ZoneDefinition[] = [
  { id: 'Z1',  name: 'Recovery',             min: 0.00, max: 0.81, color: '#9ca3af', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z2',  name: 'Aerobic',              min: 0.81, max: 0.89, color: '#3b82f6', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z3',  name: 'Tempo',                min: 0.89, max: 0.93, color: '#22c55e', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z4',  name: 'Lactate Threshold',    min: 0.93, max: 1.00, color: '#eab308', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5a', name: 'Anaerobic Endurance',  min: 1.00, max: 1.03, color: '#f97316', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5b', name: 'Aerobic Capacity',     min: 1.03, max: 1.06, color: '#ef4444', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5c', name: 'Anaerobic Capacity',   min: 1.06, max: 1.20, color: '#a855f7', method: 'Joe Friel / TrainingPeaks' },
];

// Running — Pace (% Threshold Pace) — Joe Friel / TrainingPeaks.
export const DEFAULT_RUN_ZONES_PACE: ZoneDefinition[] = [
  { id: 'Z1',  name: 'Recovery',        min: 0.00, max: 0.80, color: '#9ca3af', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z2',  name: 'Aerobic',         min: 0.80, max: 0.88, color: '#3b82f6', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z3',  name: 'Tempo',           min: 0.88, max: 0.94, color: '#22c55e', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z4',  name: 'Sub-Threshold',   min: 0.94, max: 1.00, color: '#eab308', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5a', name: 'Super-Threshold', min: 1.00, max: 1.03, color: '#f97316', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5b', name: 'VO2 Max',         min: 1.03, max: 1.11, color: '#ef4444', method: 'Joe Friel / TrainingPeaks' },
  { id: 'Z5c', name: 'Anaerobic',       min: 1.11, max: 1.40, color: '#a855f7', method: 'Joe Friel / TrainingPeaks' },
];

// Running — Power (% Critical / Threshold Power) — Stryd / Palladino.
export const DEFAULT_RUN_ZONES_POWER: ZoneDefinition[] = [
  { id: 'Z1', name: 'Easy',       min: 0.00, max: 0.80, color: '#9ca3af', method: 'Stryd / Palladino' },
  { id: 'Z2', name: 'Moderate',   min: 0.80, max: 0.90, color: '#3b82f6', method: 'Stryd / Palladino' },
  { id: 'Z3', name: 'Threshold',  min: 0.90, max: 1.00, color: '#eab308', method: 'Stryd / Palladino' },
  { id: 'Z4', name: 'Interval',   min: 1.00, max: 1.15, color: '#ef4444', method: 'Stryd / Palladino' },
  { id: 'Z5', name: 'Repetition', min: 1.15, max: 1.50, color: '#a855f7', method: 'Stryd / Palladino' },
];

// Running — HR (% LTHR) — Joe Friel.
export const DEFAULT_RUN_ZONES_HR: ZoneDefinition[] = [
  { id: 'Z1',  name: 'Recovery',           min: 0.00, max: 0.85, color: '#9ca3af', method: 'Joe Friel' },
  { id: 'Z2',  name: 'Aerobic',            min: 0.85, max: 0.89, color: '#3b82f6', method: 'Joe Friel' },
  { id: 'Z3',  name: 'Tempo',              min: 0.89, max: 0.94, color: '#22c55e', method: 'Joe Friel' },
  { id: 'Z4',  name: 'Sub-Threshold',      min: 0.94, max: 1.00, color: '#eab308', method: 'Joe Friel' },
  { id: 'Z5a', name: 'Super-Threshold',    min: 1.00, max: 1.02, color: '#f97316', method: 'Joe Friel' },
  { id: 'Z5b', name: 'Aerobic Capacity',   min: 1.02, max: 1.06, color: '#ef4444', method: 'Joe Friel' },
  { id: 'Z5c', name: 'Anaerobic Capacity', min: 1.06, max: 1.20, color: '#a855f7', method: 'Joe Friel' },
];

/** Legacy aliases kept so existing imports (DEFAULT_ZONES, DEFAULT_RUN_ZONES) keep compiling. */
export const DEFAULT_ZONES = DEFAULT_BIKE_ZONES_POWER;
export const DEFAULT_RUN_ZONES = DEFAULT_RUN_ZONES_PACE;

/** All default zone tables keyed by ZoneSetKey — used for resets, "is custom" detection, persistence. */
export const DEFAULT_ZONE_SETS: Record<ZoneSetKey, ZoneDefinition[]> = {
  'bike-power': DEFAULT_BIKE_ZONES_POWER,
  'bike-hr':    DEFAULT_BIKE_ZONES_HR,
  'run-pace':   DEFAULT_RUN_ZONES_PACE,
  'run-power':  DEFAULT_RUN_ZONES_POWER,
  'run-hr':     DEFAULT_RUN_ZONES_HR,
};

/** Sport-specific list of intensity targets the UI offers (Bike: 2, Run: 3). */
export const INTENSITY_TARGETS_BY_SPORT: Record<SportType, IntensityTarget[]> = {
  bike: ['power', 'hr'],
  run:  ['pace', 'hr', 'power'],
};

/** Compose a stable storage/lookup key from sport + intensity target. */
export function zoneSetKeyFor(sport: SportType, intensity: IntensityTarget): ZoneSetKey {
  if (sport === 'bike') {
    return intensity === 'hr' ? 'bike-hr' : 'bike-power';
  }
  if (intensity === 'pace') return 'run-pace';
  if (intensity === 'hr')   return 'run-hr';
  return 'run-power';
}

/** Translation-key returned for the "Wartości w …" header above zone inputs. */
export type UnitLabelKey = 'unitFtpPct' | 'unitHrPct' | 'unitPacePct' | 'unitRunPowerPct';

/** Pick the i18n key for the unit-of-intensity label that sits above the zone inputs. */
export function unitLabelKeyFor(sport: SportType, intensity: IntensityTarget): UnitLabelKey {
  if (sport === 'bike') return intensity === 'hr' ? 'unitHrPct' : 'unitFtpPct';
  if (intensity === 'pace')  return 'unitPacePct';
  if (intensity === 'hr')    return 'unitHrPct';
  return 'unitRunPowerPct';
}
